import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useCompleteHill } from '@/lib/use-complete-hill';
import type { HillChart } from '@hillchart/shared';
import { CheckCircle2Icon } from 'lucide-react';
import { useRef, useState } from 'react';

interface MarkAsDoneButtonProps {
  hill: HillChart;
}

/**
 * "Mark as Done" header button on the hill detail page.
 *
 * UX shape mirrors the delete dialog in `hill-list.tsx`:
 *  - AlertDialog confirmation (Radix) so the modal sits above the chart and
 *    captures focus correctly.
 *  - Synchronous double-submit guard via a ref - `mutation.isPending` flips
 *    asynchronously on the next render, so two rapid clicks (Enter held,
 *    double-click, screen-reader replay) would both pass the `isPending`
 *    check and enqueue two POSTs. The ref flips immediately inside the
 *    handler.
 *  - The dialog stays open while the request is in flight so the user sees
 *    "Marking..." rather than the dialog vanishing into a void.
 *  - Escape / overlay-click are blocked while in flight to prevent
 *    "did it actually happen?" ambiguity.
 *
 * The action is irreversible: once a hill is Done, the backend rejects every
 * subsequent write with 409 HILL_COMPLETED and the WS room is closed. The
 * dialog copy makes this explicit. The user can still delete the archived
 * hill later from the archive view.
 */
export function MarkAsDoneButton({ hill }: MarkAsDoneButtonProps) {
  const [open, setOpen] = useState(false);
  const submittingRef = useRef(false);
  const completeMutation = useCompleteHill(hill.id);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (completeMutation.isPending) return;
        setOpen(next);
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="default" size="sm" data-testid="mark-as-done-button">
          <CheckCircle2Icon className="h-4 w-4" />
          Mark as Done
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark this hill as Done?</AlertDialogTitle>
          <AlertDialogDescription>
            This locks <strong>{hill.title}</strong> in read-only mode. A final snapshot will be
            saved automatically. Other team members will lose live editing access.{' '}
            <strong>This action cannot be undone</strong>, you can still delete the archived hill
            later from the Archive view.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={completeMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // preventDefault keeps the dialog open while the mutation
              // runs. We close it explicitly in onSuccess so the user sees
              // "Marking..." rather than a flicker between dialog and the
              // post-completion read-only state.
              e.preventDefault();
              if (submittingRef.current) return;
              submittingRef.current = true;
              completeMutation.mutate(undefined, {
                onSettled: () => {
                  submittingRef.current = false;
                },
                onSuccess: () => {
                  setOpen(false);
                },
              });
            }}
            disabled={completeMutation.isPending}
            data-testid="mark-as-done-confirm"
          >
            {completeMutation.isPending ? 'Marking…' : 'Mark as Done'}
          </AlertDialogAction>
        </AlertDialogFooter>
        {completeMutation.isError ? (
          <p className="px-1 text-red-600 text-sm" role="alert">
            Could not mark as Done: {completeMutation.error.message}
          </p>
        ) : null}
      </AlertDialogContent>
    </AlertDialog>
  );
}
